import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./api";
import "./admin.css";

const FILE_RULES = {
     image: {
          types: ["image/webp"],
          accept: ".webp, image/webp",
          maxMb: 10,
          name: "a WebP image",
     },
     audio: {
          types: ["audio/mpeg"],
          accept: ".mp3",
          maxMb: 200,
          name: "an MP3 file",
     },
};

const isFileField = (f) => f.type === "image" || f.type === "audio";
const today = () => new Date().toISOString().slice(0, 10);
const plainLabel = (f) => f.label.replace(/\s*\(.*\)/, "").toLowerCase();
const blankValues = (typeCfg) =>
     Object.fromEntries(typeCfg.fields.filter((f) => !isFileField(f)).map((f) => [f.key, ""]));
const formatDate = (d) =>
     d
          ? new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", {
               day: "numeric",
               month: "short",
               year: "numeric",
          })
          : "";
const formatSize = (bytes) =>
     bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;

export default function ContentAdmin({ client }) {
     const [authed, setAuthed] = useState(Boolean(api.getToken()));
     const [notice, setNotice] = useState("");
     const [config, setConfig] = useState(null);
     const [configError, setConfigError] = useState("");
     const [view, setView] = useState("menu"); // "menu", "password", or a content type key

     // Keep this page out of search results.
     useEffect(() => {
          const previousTitle = document.title;
          document.title = "Manage content";
          const meta = document.createElement("meta");
          meta.name = "robots";
          meta.content = "noindex, nofollow";
          document.head.appendChild(meta);
          return () => {
               document.title = previousTitle;
               meta.remove();
          };
     }, []);

     useEffect(() => {
          let cancelled = false;
          api
               .getConfig(client)
               .then((c) => {
                    if (cancelled) return;
                    setConfig(c);
               })
               .catch((err) => !cancelled && setConfigError(err.message));
          return () => {
               cancelled = true;
          };
     }, [client]);

     const handleExpired = useCallback(() => {
          api.clearToken();
          setAuthed(false);
          setNotice("Your session ended. Log in again to continue.");
          setView("menu");
     }, []);

     function handleLogout() {
          api.clearToken();
          setNotice("");
          setAuthed(false);
          setView("menu");
     }

     return (
          <main className="ca">
               <header className="ca-header">
                    <h1>{config ? config.name : "Manage content"}</h1>
                    {authed && (
                         <button type="button" className="ca-link" onClick={handleLogout}>
                              Log out
                         </button>
                    )}
               </header>

               {configError && (
                    <p className="ca-error" role="alert">
                         {configError}
                    </p>
               )}

               {!config && !configError && <p className="ca-muted">Loading…</p>}

               {config && !authed && (
                    <LoginForm client={client} notice={notice} onLoggedIn={() => setAuthed(true)} />
               )}

               {config && authed && view === "menu" && <Menu config={config} onPick={setView} />}

               {config && authed && view !== "menu" && (
                    <>
                         <button type="button" className="ca-link ca-back" onClick={() => setView("menu")}>
                              ← Back to menu
                         </button>
                         {view === "password" && <ChangePassword client={client} onExpired={handleExpired} />}
                         {config.contentTypes[view] && (
                              <ContentSection
                                   key={view}
                                   client={client}
                                   type={view}
                                   typeCfg={config.contentTypes[view]}
                                   onExpired={handleExpired}
                              />
                         )}
                    </>
               )}
          </main>
     );
}

function LoginForm({ client, notice, onLoggedIn }) {
     const [password, setPassword] = useState("");
     const [error, setError] = useState("");
     const [busy, setBusy] = useState(false);

     async function handleSubmit(e) {
          e.preventDefault();
          setBusy(true);
          setError("");
          try {
               const { token, role } = await api.login(client, password);
               api.setToken(token);
               api.setRole(role);
               onLoggedIn();
          } catch (err) {
               setError(err.status === 401 ? "That password isn't right." : err.message);
               setBusy(false);
          }
     }

     return (
          <form className="ca-panel ca-login" onSubmit={handleSubmit}>
               <h2>Log in</h2>
               {notice && <p className="ca-muted">{notice}</p>}
               <div className="ca-field">
                    <label htmlFor="ca-password">Password</label>
                    <input
                         id="ca-password"
                         type="password"
                         autoComplete="current-password"
                         value={password}
                         onChange={(e) => setPassword(e.target.value)}
                         required
                    />
               </div>
               {error && (
                    <p className="ca-error" role="alert">
                         {error}
                    </p>
               )}
               <button type="submit" className="ca-btn" disabled={busy || !password}>
                    {busy ? "Logging in…" : "Log in"}
               </button>
          </form>
     );
}

function ContentSection({ client, type, typeCfg, onExpired }) {
     const [entries, setEntries] = useState([]);
     const [loading, setLoading] = useState(true);
     const [loadError, setLoadError] = useState("");
     const [editing, setEditing] = useState(null); // the entry being edited, or null when adding
     const [notice, setNotice] = useState("");

     const load = useCallback(async () => {
          try {
               const { items } = await api.listEntries(client, type);
               setEntries(items);
               setLoadError("");
          } catch (err) {
               if (err.status === 401) return onExpired();
               setLoadError(err.message);
          } finally {
               setLoading(false);
          }
     }, [client, type, onExpired]);

     useEffect(() => {
          load();
     }, [load]);

     function startEdit(entry) {
          setNotice("");
          setEditing(entry);
          window.scrollTo({ top: 0, behavior: "smooth" });
     }

     function handleSaved(message) {
          setNotice(message || "");
          setEditing(null);
          return load();
     }

     async function handleDelete(id) {
          try {
               await api.deleteEntry(client, type, id);
               if (editing && editing.id === id) setEditing(null);
               await load();
               return null;
          } catch (err) {
               if (err.status === 401) {
                    onExpired();
                    return null;
               }
               return err.message;
          }
     }

     return (
          <>
               {notice && (
                    <p className="ca-status ca-success" role="status" style={{ marginBottom: 16 }}>
                         {notice}
                    </p>
               )}
               <EntryForm
                    key={editing ? editing.id : "new"} // a different entry gets a fresh form
                    client={client}
                    type={type}
                    typeCfg={typeCfg}
                    entry={editing}
                    onSaved={handleSaved}
                    onCancel={() => setEditing(null)}
                    onExpired={onExpired}
               />
               <EntryList
                    entries={entries}
                    typeCfg={typeCfg}
                    loading={loading}
                    error={loadError}
                    editingId={editing ? editing.id : null}
                    onEdit={startEdit}
                    onDelete={handleDelete}
               />
          </>
     );
}

const valuesFromEntry = (typeCfg, entry) =>
     Object.fromEntries(
          typeCfg.fields.filter((f) => !isFileField(f)).map((f) => [f.key, entry[f.key] ?? ""])
     );
const existingFilesFromEntry = (typeCfg, entry) =>
     Object.fromEntries(typeCfg.fields.filter((f) => isFileField(f)).map((f) => [f.key, entry[f.key] || null]));

// Adds a new entry, or edits `entry` when one is passed in.
function EntryForm({ client, type, typeCfg, entry, onSaved, onCancel, onExpired }) {
     const editing = Boolean(entry);
     const [values, setValues] = useState(() =>
          editing ? valuesFromEntry(typeCfg, entry) : blankValues(typeCfg)
     );
     const [existingFiles, setExistingFiles] = useState(() =>
          editing ? existingFilesFromEntry(typeCfg, entry) : {}
     ); // files already saved with the entry (field key -> URL)
     const [files, setFiles] = useState({}); // newly chosen files (field key -> File)
     const [errors, setErrors] = useState({});
     const [status, setStatus] = useState({ kind: "idle", message: "" });
     const [formKey, setFormKey] = useState(0); // bumping this clears the file inputs
     const working = status.kind === "working";

     const setValue = (key, value) => setValues((v) => ({ ...v, [key]: value }));

     // Returns false if the file is rejected, so the input can reset itself.
     function pickFile(field, file) {
          const rule = FILE_RULES[field.type];
          let problem = "";
          if (file && !rule.types.includes(file.type)) problem = `Choose ${rule.name}.`;
          else if (file && file.size > rule.maxMb * 1024 * 1024)
               problem = `That file is over ${rule.maxMb} MB.`;
          setErrors((e) => ({ ...e, [field.key]: problem }));
          if (problem) return false;
          setFiles((f) => ({ ...f, [field.key]: file || undefined }));
          return true;
     }

     async function handleSubmit(e) {
          e.preventDefault();
          const problems = {};
          for (const f of typeCfg.fields) {
               if (isFileField(f)) {
                    if (f.required && !files[f.key] && !existingFiles[f.key])
                         problems[f.key] = `${f.label} is required.`;
               } else if (f.required && !values[f.key].trim()) {
                    problems[f.key] = `${f.label} is required.`;
               } else if (f.maxLength && values[f.key].length > f.maxLength) {
                    problems[f.key] = `${f.label} is ${values[f.key].length - f.maxLength} characters too long.`;
               }
          }

          setErrors(problems);
          if (Object.keys(problems).length) {
               setStatus({ kind: "error", message: "Fix the highlighted fields and try again." });
               return;
          }

          setStatus({ kind: "working", message: editing ? "Saving…" : "Publishing…" });
          try {
               const body = { ...values };
               for (const f of typeCfg.fields.filter(isFileField)) {
                    if (files[f.key]) {
                         setStatus({ kind: "working", message: `Uploading ${plainLabel(f)}…` });
                         body[f.key] = await api.uploadFile(client, type, f.key, files[f.key], values[typeCfg.fileNameFrom]);
                    } else {
                         // Keep the saved file, or send "" if there isn't one (or it was removed).
                         body[f.key] = existingFiles[f.key] || "";
                    }
               }
               setStatus({ kind: "working", message: "Saving…" });

               if (editing) {
                    await api.updateEntry(client, type, entry.id, body);
                    onSaved("Changes saved.");
                    return; // this form is replaced by a fresh one
               }

               await api.createEntry(client, type, body);
               const scheduled = values.releaseDate && values.releaseDate > today();
               setStatus({
                    kind: "success",
                    message: scheduled ? `Scheduled for ${formatDate(values.releaseDate)}.` : "Published.",
               });
               setValues(blankValues(typeCfg));
               setFiles({});
               setFormKey((k) => k + 1);
               onSaved("");
          } catch (err) {
               if (err.status === 401) return onExpired();
               setStatus({ kind: "error", message: err.message });
          }
     }

     return (
          <form className="ca-panel" onSubmit={handleSubmit} key={formKey} noValidate>
               <h2>{editing ? "Edit entry" : `Add to ${typeCfg.label}`}</h2>
               {editing && (
                    <p className="ca-muted">Changes appear on the website as soon as you save.</p>
               )}

               {typeCfg.fields.map((f) => {
                    const id = `ca-${type}-${f.key}`;
                    const error = errors[f.key];
                    return (
                         <div className="ca-field" key={f.key}>
                              <label htmlFor={id}>{f.label}</label>

                              {f.type === "text" && (
                                   <input
                                        id={id}
                                        type="text"
                                        maxLength={f.maxLength ?? 300}
                                        value={values[f.key]}
                                        onChange={(e) => setValue(f.key, e.target.value)}
                                        aria-invalid={Boolean(error)}
                                   />
                              )}
                              {f.type === "date" && (
                                   <input
                                        id={id}
                                        type="date"
                                        value={values[f.key]}
                                        onChange={(e) => setValue(f.key, e.target.value)}
                                        aria-invalid={Boolean(error)}
                                   />
                              )}
                              {f.type === "textarea" && (
                                   <FormattedTextarea
                                        id={id}
                                        value={values[f.key]}
                                        onChange={(v) => setValue(f.key, v)}
                                        plain={Boolean(f.plain)}
                                        invalid={Boolean(error)}
                                        maxLength={f.maxLength}
                                   />
                              )}
                              {isFileField(f) && (
                                   <FileInput
                                        id={id}
                                        field={f}
                                        file={files[f.key]}
                                        existingUrl={existingFiles[f.key]}
                                        canRemoveExisting={!f.required}
                                        onRemoveExisting={() => setExistingFiles((x) => ({ ...x, [f.key]: null }))}
                                        onPick={(file) => pickFile(f, file)}
                                   />
                              )}

                              {error && (
                                   <p className="ca-error" role="alert">
                                        {error}
                                   </p>
                              )}
                         </div>
                    );
               })}

               <div className="ca-actions">
                    <button type="submit" className="ca-btn" disabled={working}>
                         {working ? "Please wait…" : editing ? "Save changes" : "Publish"}
                    </button>
                    {editing && (
                         <button type="button" className="ca-btn ca-btn-secondary" onClick={onCancel} disabled={working}>
                              Cancel
                         </button>
                    )}
                    <p
                         className={`ca-status ${status.kind === "error" ? "ca-error" : ""} ${status.kind === "success" ? "ca-success" : ""
                              }`}
                         role="status"
                         aria-live="polite"
                    >
                         {status.message}
                    </p>
               </div>
          </form>
     );
}

// A textarea with a Bold button (and Ctrl/Cmd+B). Bold text is stored as **text**,
// so the client never has to see or write HTML. Set "plain": true on a field in the
// config to hide the Bold button for that field.
function FormattedTextarea({ id, value, onChange, invalid, plain, maxLength }) {
     const ref = useRef(null);

     function makeBold() {
          const el = ref.current;
          if (!el) return;
          const start = el.selectionStart;
          const end = el.selectionEnd;
          const selected = value.slice(start, end);
          onChange(`${value.slice(0, start)}**${selected}**${value.slice(end)}`);
          requestAnimationFrame(() => {
               el.focus();
               const cursor = selected ? end + 4 : start + 2; // after the closing ** or between the pair
               el.setSelectionRange(cursor, cursor);
          });
     }

     return (
          <>
               <div className="ca-rich">
                    {!plain && (
                         <div className="ca-toolbar">
                              <button type="button" className="ca-btn ca-btn-small" onClick={makeBold}>
                                   <strong>Bold</strong>
                              </button>
                              <span className="ca-muted">
                                   Select text, then press Bold. Leave a blank line between paragraphs.
                              </span>
                         </div>
                    )}
                    <textarea
                         id={id}
                         ref={ref}
                         rows={plain ? 5 : 10}
                         value={value}
                         onChange={(e) => onChange(e.target.value)}
                         onKeyDown={(e) => {
                              if (!plain && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b") {
                                   e.preventDefault();
                                   makeBold();
                              }
                         }}
                         aria-invalid={invalid}
                    />
               </div>
               <CharCount value={value} max={maxLength} />
          </>
     );
}

function FileInput({ id, field, file, existingUrl, canRemoveExisting, onRemoveExisting, onPick }) {
     const rule = FILE_RULES[field.type];
     const inputRef = useRef(null);
     const [previewUrl, setPreviewUrl] = useState("");
     const kindName = field.type === "image" ? "image" : "audio file";

     useEffect(() => {
          if (!file || field.type !== "image") {
               setPreviewUrl("");
               return undefined;
          }
          const url = URL.createObjectURL(file);
          setPreviewUrl(url);
          return () => URL.revokeObjectURL(url);
     }, [file, field.type]);

     function clear() {
          if (inputRef.current) inputRef.current.value = "";
          onPick(null);
     }

     return (
          <div className="ca-file">
               <input
                    id={id}
                    ref={inputRef}
                    type="file"
                    accept={rule.accept}
                    onChange={(e) => {
                         const ok = onPick(e.target.files[0] || null);
                         if (!ok) e.target.value = "";
                    }}
               />
               {field.type === "image" && (
                    <p className="ca-muted ca-hint">
                         WebP files only. <br /> You can convert your file for free at{" "}
                         <a href="https://squoosh.app" target="_blank" rel="noopener noreferrer">
                              squoosh.app
                         </a>{" "}
                    </p>
               )}
               {field.type === "audio" && (
                    <p className="ca-muted ca-hint">
                         MP3 files only. <br /> You can convert your file for free at{" "}
                         <a href="https://castos.com/tools/audio-converter/" target="_blank" rel="noopener noreferrer">
                              castos.com
                         </a>{" "}
                    </p>
               )}
               {existingUrl && !file && (
                    <div className="ca-file-chosen">
                         {field.type === "image" ? (
                              <img src={existingUrl} alt="" className="ca-thumb" />
                         ) : (
                              <audio controls preload="none" src={existingUrl} />
                         )}
                         <span>Current {kindName}. Choose a new file above to replace it.</span>
                         {canRemoveExisting && (
                              <button type="button" className="ca-link" onClick={onRemoveExisting}>
                                   Remove
                              </button>
                         )}
                    </div>
               )}
               {file && (
                    <div className="ca-file-chosen">
                         {previewUrl && <img src={previewUrl} alt="" className="ca-thumb" />}
                         <span>
                              {file.name} ({formatSize(file.size)})
                              {existingUrl ? ` will replace the current ${kindName}.` : ""}
                         </span>
                         <button type="button" className="ca-link" onClick={clear}>
                              Remove
                         </button>
                    </div>
               )}
          </div>
     );
}

function EntryList({ entries, typeCfg, loading, error, editingId, onEdit, onDelete }) {
     const [confirmingId, setConfirmingId] = useState(null);
     const [deletingId, setDeletingId] = useState(null);
     const [deleteError, setDeleteError] = useState("");

     const titleKey =
          typeCfg.fields.find((f) => f.key === "title")?.key ||
          typeCfg.fields.find((f) => f.type === "text")?.key;
     const imageKey = typeCfg.fields.find((f) => f.type === "image")?.key;

     async function confirmDelete(id) {
          setDeletingId(id);
          setDeleteError("");
          const problem = await onDelete(id);
          setDeletingId(null);
          setConfirmingId(null);
          if (problem) setDeleteError(problem);
     }

     return (
          <section className="ca-panel" aria-labelledby="ca-list-heading">
               <h2 id="ca-list-heading">Current entries</h2>
               {error && (
                    <p className="ca-error" role="alert">
                         {error}
                    </p>
               )}
               {deleteError && (
                    <p className="ca-error" role="alert">
                         {deleteError}
                    </p>
               )}
               {loading && <p className="ca-muted">Loading…</p>}
               {!loading && !error && entries.length === 0 && (
                    <p className="ca-muted">Nothing here yet. Use the form above to add the first one.</p>
               )}

               <ul className="ca-list">
                    {entries.map((entry) => {
                         const scheduled = entry.releaseDate && entry.releaseDate > today();
                         const title = (titleKey && entry[titleKey]) || "Untitled";
                         const isEditing = editingId === entry.id;
                         return (
                              <li key={entry.id} className="ca-item">
                                   {imageKey && entry[imageKey] && (
                                        <img src={entry[imageKey]} alt="" className="ca-thumb" loading="lazy" />
                                   )}
                                   <div className="ca-item-text">
                                        <strong>{title}</strong>
                                        <span className="ca-muted">
                                             {formatDate(entry.releaseDate)}
                                             {scheduled && <span className="ca-badge">Scheduled</span>}
                                             {isEditing && <span className="ca-badge">Editing</span>}
                                        </span>
                                   </div>
                                   {confirmingId === entry.id ? (
                                        <div className="ca-confirm">
                                             <span>Delete this entry and its files?</span>
                                             <button
                                                  type="button"
                                                  className="ca-btn ca-btn-danger ca-btn-small"
                                                  disabled={deletingId === entry.id}
                                                  onClick={() => confirmDelete(entry.id)}
                                             >
                                                  {deletingId === entry.id ? "Deleting…" : "Yes, delete"}
                                             </button>
                                             <button
                                                  type="button"
                                                  className="ca-btn ca-btn-secondary ca-btn-small"
                                                  disabled={deletingId === entry.id}
                                                  onClick={() => setConfirmingId(null)}
                                             >
                                                  Keep
                                             </button>
                                        </div>
                                   ) : (
                                        <div className="ca-confirm">
                                             <button
                                                  type="button"
                                                  className="ca-btn ca-btn-secondary ca-btn-small"
                                                  disabled={isEditing}
                                                  onClick={() => onEdit(entry)}
                                             >
                                                  Edit
                                             </button>
                                             <button
                                                  type="button"
                                                  className="ca-btn ca-btn-secondary ca-btn-small"
                                                  onClick={() => setConfirmingId(entry.id)}
                                             >
                                                  Delete
                                             </button>
                                        </div>
                                   )}
                              </li>
                         );
                    })}
               </ul>
          </section>
     );
}

function Menu({ config, onPick }) {
     return (
          <section className="ca-panel" aria-labelledby="ca-menu-heading">
               <h2 id="ca-menu-heading">What would you like to change?</h2>
               <ul className="ca-menu">
                    {Object.entries(config.contentTypes).map(([key, t]) => (
                         <li key={key}>
                              <button type="button" className="ca-menu-item" onClick={() => onPick(key)}>
                                   <strong>{t.label}</strong>
                                   {t.description && <span className="ca-muted">{t.description}</span>}
                              </button>
                         </li>
                    ))}
                    <li>
                         <button type="button" className="ca-menu-item" onClick={() => onPick("password")}>
                              <strong>Change password</strong>
                         </button>
                    </li>
               </ul>
          </section>
     );
}

function ChangePassword({ client, onExpired }) {
     const isMaster = api.getRole() === "master";
     const [current, setCurrent] = useState("");
     const [next, setNext] = useState("");
     const [again, setAgain] = useState("");
     const [status, setStatus] = useState({ kind: "idle", message: "" });

     async function handleSubmit(e) {
          e.preventDefault();
          if (next.length < 10)
               return setStatus({ kind: "error", message: "Use at least 10 characters for the new password." });
          if (next !== again)
               return setStatus({ kind: "error", message: "The two new passwords don't match." });

          setStatus({ kind: "working", message: "Saving…" });
          try {
               await api.changePassword(client, current, next);
               setCurrent("");
               setNext("");
               setAgain("");
               setStatus({ kind: "success", message: "Password changed." });
          } catch (err) {
               if (err.status === 401) return onExpired(); // session ended
               setStatus({ kind: "error", message: err.message }); // e.g. 403: wrong current password
          }
     }

     return (
          <form className="ca-panel" onSubmit={handleSubmit}>
               <h2>Change password</h2>
               {isMaster && (
                    <p className="ca-muted">
                         You're logged in as a Master Admin.
                    </p>
               )}
               {!isMaster && (
                    <div className="ca-field">
                         <label htmlFor="ca-current">Current password</label>
                         <input id="ca-current" type="password" autoComplete="current-password"
                              value={current} onChange={(e) => setCurrent(e.target.value)} />
                    </div>
               )}
               <div className="ca-field">
                    <label htmlFor="ca-new">New password</label>
                    <input id="ca-new" type="password" autoComplete="new-password"
                         value={next} onChange={(e) => setNext(e.target.value)} />
                    <p className="ca-muted ca-hint">At least 10 characters. A few unrelated words works well.</p>
               </div>
               <div className="ca-field">
                    <label htmlFor="ca-again">New password again</label>
                    <input id="ca-again" type="password" autoComplete="new-password"
                         value={again} onChange={(e) => setAgain(e.target.value)} />
               </div>
               <div className="ca-actions">
                    <button type="submit" className="ca-btn" disabled={status.kind === "working"}>
                         Change password
                    </button>
                    <p
                         className={`ca-status ${status.kind === "error" ? "ca-error" : ""} ${status.kind === "success" ? "ca-success" : ""
                              }`}
                         role="status"
                         aria-live="polite"
                    >
                         {status.message}
                    </p>
               </div>
          </form>
     );
}

// Shows "312 / 350 characters" under a field that has a maxLength in the config.
function CharCount({ value, max }) {
     if (!max) return null;
     const over = value.length - max;
     return (
          <p className={`ca-muted ca-hint ${over > 0 ? "ca-error" : ""}`}>
               {value.length} / {max} characters{over > 0 ? ` (${over} too many)` : ""}
          </p>
     );
}